import { useQuery } from '@tanstack/vue-query';
import type { DeveloperIndexRow, DeveloperStatusMix } from '~/api/queries/useDeveloperIndexQuery';

export type DeveloperDetail = DeveloperIndexRow & {
  salesManagers: Array<{ name?: string; phone?: string; whatsapp?: string; email?: string; role?: string }>;
  allCommunities: string[];
  activityCount90d: number;
};

export type HandoverBucket = { year: number; q1: number; q2: number; q3: number; q4: number };

export type DeveloperHandover = {
  buckets: HandoverBucket[];
  tba: number;
  peakYear: number | null;
  peakQuarter: number | null;
  peakCount: number;
};

export type DeveloperActivityItem = {
  id: number;
  timestamp: string;
  category: 'LAUNCH' | 'BROCHURE' | 'UPDATE' | 'INVENTORY' | 'INTEL' | 'HANDOVER';
  actionType: string;
  summary: string | null;
  projectId: number | null;
  projectName: string | null;
  projectIsNew: boolean;
};

export type DeveloperDetailPayload = {
  developer: DeveloperDetail;
  handover: DeveloperHandover;
  activity: DeveloperActivityItem[];
};

// The endpoint is PUBLIC — guests get a redacted view (no commission, no
// sales-manager contacts). When an auth token is available we pass it so
// the server returns the full payload for the logged-in agent.
export const useDeveloperDetailQuery = (slug: MaybeRef<string>) => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: computed(() => ['developers', 'detail', unref(slug), !!authStore.token]),
    queryFn: () =>
      $fetch<{ success: boolean; data: DeveloperDetailPayload }>(
        `/api/developers/by-slug/${encodeURIComponent(unref(slug))}`,
        authStore.token
          ? { headers: { Authorization: `Bearer ${authStore.token}` } }
          : {},
      ),
    enabled: computed(() => !!unref(slug)),
    select: (res) => res?.data ?? null,
    staleTime: 60_000,
    retry: false,
    throwOnError: false,
  });
};
